import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import EmployeeService from '../../services/employee.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await EmployeeService.getAll({ search: searchTerm });
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      showSnackbar(
        `${t('employees.errorLoadingEmployees')}: ${error.response?.data?.message || error.message}`,
        'error'
      );
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchEmployees();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await EmployeeService.delete(deleteDialog.id);
      showSnackbar(t('employees.employeeDeletedSuccess'));
      fetchEmployees();
    } catch (error) {
      showSnackbar(error.response?.data?.message || t('employees.errorDeletingEmployee'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const roleLabel = (role) => {
    const map = {
      cleaner: t('employees.roleCleaner'),
      supervisor: t('employees.roleSupervisor'),
      admin: t('employees.roleAdmin'),
    };
    return map[role] || role;
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('employees.title')}</Typography>
            {hasPermission('employees.create') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/employees/new')}
              >
                {t('employees.newEmployee')}
              </Button>
            )}
          </Box>

          <TextField
            placeholder={t('employees.searchEmployees')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('employees.name')}</TableCell>
                  <TableCell>{t('employees.email')}</TableCell>
                  <TableCell>{t('employees.phone')}</TableCell>
                  <TableCell>{t('employees.role')}</TableCell>
                  <TableCell>{t('employees.status')}</TableCell>
                  <TableCell align="right">{t('employees.hourlyRate')}</TableCell>
                  <TableCell>{t('employees.color')}</TableCell>
                  <TableCell align="center">{t('employees.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {t('employees.noEmployeesFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((emp) => (
                    <TableRow key={emp.id} hover>
                      <TableCell>{emp.name || '-'}</TableCell>
                      <TableCell>{emp.email || '-'}</TableCell>
                      <TableCell>{emp.phone || '-'}</TableCell>
                      <TableCell>
                        <Chip label={roleLabel(emp.role)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            emp.status === 'active'
                              ? t('employees.statusActive')
                              : t('employees.statusInactive')
                          }
                          size="small"
                          color={emp.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {emp.hourly_rate != null ? Number(emp.hourly_rate).toFixed(2) : '-'}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: emp.color || '#ccc',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                          title={emp.color}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('employees.edit') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/employees/${emp.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission('employees.delete') && (
                            <IconButton size="small" color="error" onClick={() => handleDelete(emp.id)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>{t('employees.deleteEmployee')}</DialogTitle>
        <DialogContent>
          <Typography>{t('employees.confirmDeletion')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>{t('common.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeList;
